import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config';
import './PeriodizationTimeline.css';

interface TrainingCycle {
  cycle_type: string;
  pattern: string;
  start_date: string;
  end_date: string;
  focus_zones: string[] | null;
  priority_events: Array<{ name: string; date: string; priority?: string }> | null;
  target_weekly_tss_low: number | null;
  target_weekly_tss_high: number | null;
}

interface PeriodizationTimelineProps {
  targetUserId?: number | null;
}

const PeriodizationTimeline: React.FC<PeriodizationTimelineProps> = ({ targetUserId }) => {
  const [cycle, setCycle] = useState<TrainingCycle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCycle = async () => {
      try {
        const params = targetUserId ? `?target_user_id=${targetUserId}` : '';
        const res = await fetch(`${API_BASE_URL}/api/web/coach/training-cycle${params}`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setCycle(data);
        }
      } catch (e) {
        console.error('Failed to fetch training cycle:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCycle();
  }, [targetUserId]);

  if (loading) {
    return (
      <div className="period-timeline-card">
        <div className="period-timeline-loading">Loading cycle...</div>
      </div>
    );
  }

  if (!cycle) {
    return (
      <div className="period-timeline-card">
        <div className="period-timeline-empty">
          No training cycle active. Ask your AI coach to set one up.
        </div>
      </div>
    );
  }

  // Parse pattern (e.g., "3:1" => 3 load, 1 recovery)
  const patternParts = cycle.pattern.split(':').map(Number);
  const loadWeeks = patternParts[0] || 3;
  const recoveryWeeks = patternParts[1] || 1;
  const patternLength = loadWeeks + recoveryWeeks;

  // Calculate weeks
  const startDate = new Date(cycle.start_date + 'T00:00:00');
  const endDate = new Date(cycle.end_date + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totalDays = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(totalDays / 7);

  // Build week blocks
  const weeks: Array<{ weekNum: number; isLoad: boolean; startDate: Date; endDate: Date; isCurrent: boolean }> = [];
  for (let i = 0; i < totalWeeks; i++) {
    const weekStart = new Date(startDate);
    weekStart.setDate(weekStart.getDate() + i * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const posInPattern = i % patternLength;
    const isLoad = posInPattern < loadWeeks;
    const isCurrent = today >= weekStart && today <= weekEnd;

    weeks.push({ weekNum: i + 1, isLoad, startDate: weekStart, endDate: weekEnd, isCurrent });
  }

  // Map events to timeline positions
  const events = (cycle.priority_events || []).map(ev => {
    const evDate = new Date(ev.date + 'T00:00:00');
    const dayOffset = (evDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
    const pct = Math.max(0, Math.min(100, (dayOffset / totalDays) * 100));
    return { ...ev, pct };
  });

  // Current week marker position
  const todayOffset = (today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  const todayPct = Math.max(0, Math.min(100, (todayOffset / totalDays) * 100));
  const showTodayMarker = today >= startDate && today <= endDate;

  // TSS range text
  const tssText = cycle.target_weekly_tss_low && cycle.target_weekly_tss_high
    ? `${Math.round(cycle.target_weekly_tss_low)}-${Math.round(cycle.target_weekly_tss_high)} TSS/week`
    : null;

  return (
    <div className="period-timeline-card">
      <div className="period-timeline-header">
        <h3 className="period-timeline-title">Training Cycle</h3>
        <span className="period-timeline-dates">
          {formatShortDate(startDate)} - {formatShortDate(endDate)}
        </span>
      </div>

      <div className="period-timeline-bar-container">
        <div className="period-timeline-bar">
          {weeks.map((w, i) => (
            <div
              key={i}
              className={`period-timeline-week ${w.isLoad ? 'load' : 'recovery'} ${w.isCurrent ? 'current' : ''}`}
              style={{ width: `${100 / totalWeeks}%` }}
              title={`Week ${w.weekNum}: ${w.isLoad ? 'Load' : 'Recovery'}`}
            />
          ))}
        </div>

        {showTodayMarker && (
          <div className="period-timeline-today" style={{ left: `${todayPct}%` }}>
            <div className="period-timeline-today-line" />
            <div className="period-timeline-today-label">Today</div>
          </div>
        )}

        {events.map((ev, i) => {
          const priorityColor = ev.priority === 'A' ? '#1a1a1a' : ev.priority === 'B' ? '#666' : '#bbb';
          return (
            <div key={i} className="period-timeline-event" style={{ left: `${ev.pct}%` }}
              title={`${ev.priority || '?'}: ${ev.name} (${ev.date})`}>
              <svg width="12" height="12" viewBox="0 0 12 12">
                <rect x="6" y="0" width="8" height="8" rx="1" transform="rotate(45 6 0)" fill={priorityColor} />
              </svg>
              {ev.priority === 'A' && <span className="period-event-label">{ev.name}</span>}
            </div>
          );
        })}
      </div>

      <div className="period-timeline-meta">
        <span className="period-timeline-badge">{cycle.cycle_type}</span>
        <span className="period-timeline-pattern">{cycle.pattern}</span>
        {tssText && <span className="period-timeline-tss">{tssText}</span>}
        {cycle.focus_zones && cycle.focus_zones.length > 0 && (
          <div className="period-timeline-zones">
            {cycle.focus_zones.map((z, i) => (
              <span key={i} className="period-timeline-zone-tag">{z}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function formatShortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default PeriodizationTimeline;
